import { Component, DestroyRef, inject, input, output } from '@angular/core';
import { MatCard, MatCardContent, MatCardFooter } from '@angular/material/card';
import { MatDivider } from '@angular/material/divider';
import { MatList, MatListItem } from '@angular/material/list';
import { MatTooltip } from '@angular/material/tooltip';
import { SbInviteMxidDialogComponent } from '../sb-invite-mxid-dialog/sb-invite-mxid-dialog.component';
import { ButtonComponent } from '../../../common/components/button/button.component';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { DropdownComponent } from '../../../common/components/dropdown/dropdown.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserIconComponent } from '../user-icon/user-icon.component';
import { Router } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { SbPinnedMessageComponent } from '../sb-pinned-message/sb-pinned-message.component';
import { PinnedMessageService } from '../../../services/pinMessages/pinned-message.service';
import { MatrixService } from '../../../services/matrix/matrix.service';
import { SbUserProfileCardComponent } from '../sb-user-profile-card/sb-user-profile-card.component';
import { blockRoom, Room as ModelRoom, unblockRoom } from '../../../model/room';
import { RoomMember } from '../../../model/room-member';
import { PresenceService } from '../../../services/presence/presence.service';
import { catchError, EMPTY, expand, finalize, from, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { DialogData } from '../../../common/components/dialog/dialog.component';
import { DialogService } from '../../../services/dialog/dialog.service';
import { ArchiveService } from '../../../services/archive/archive.service';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { RoomMembershipState } from '../../../../shared-api/roomMembershipState';
import { KnownMembership } from 'matrix-js-sdk/lib/@types/membership.js';
import { EventTimeline, MatrixEvent, TimelineWindow } from 'matrix-js-sdk';
import { KickService } from '../../../services/kick/kick.service';
import { RoomService } from '../../../services/room/room.service';
import { RoomHelperService } from '../../../services/room/room-helper.service';
import { RoomListAccessService } from '../../../services/room-list/room-list-access.service';
import { ChatStoreService } from '../../../services/chat-store/chat-store.service';
import { RoomSidebarTestIds } from './sb-room-sidebar.test-ids';
import { ChipComponent } from '../../core/chip/chip.component';
import { MatChipSet } from '@angular/material/chips';
import { RoomHistoryVisibility } from '../../../model/room-history-visibility';

@Component({
  selector: 'app-sb-room-sidebar',
  imports: [
    MatCard,
    MatCardContent,
    MatDivider,
    MatList,
    MatListItem,
    MatTooltip,
    SbInviteMxidDialogComponent,
    ButtonComponent,
    MatCardFooter,
    MatIcon,
    MatIconButton,
    DropdownComponent,
    UserIconComponent,
    AsyncPipe,
    SbPinnedMessageComponent,
    SbUserProfileCardComponent,
    ChipComponent,
    MatChipSet
  ],
  templateUrl: './sb-room-sidebar.component.html',
  styleUrl: './sb-room-sidebar.component.scss'
})
/**
 * Hierdurch erfüllte AFO`s
 * - A_25562-01 - Hinweis auf Öffentlichkeit eines Raums
 * - A_26463 - Sperrung der weiterführenden Kommunikation nach Verlassen des Raumes
 */
export class SbRoomSidebarComponent {
  public opened = input.required<boolean>();

  sidebarOpenedChange = output<boolean>();
  selectedMemberId: string | null = null;
  hoveredMemberId: string | null = null;
  hoverTimeout: ReturnType<typeof setTimeout> | undefined;

  protected topic = '';
  protected readonly KnownMembership = KnownMembership;
  protected readonly testIds = RoomSidebarTestIds;
  protected readonly RoomMembershipState = RoomMembershipState;

  protected readonly clientService = inject(MatrixService);
  protected readonly roomListAccessService = inject(RoomListAccessService);
  protected readonly chatStoreService = inject(ChatStoreService);
  protected readonly roomHelperService = inject(RoomHelperService);
  protected readonly roomService = inject(RoomService);
  protected readonly presenceService = inject(PresenceService);
  protected readonly router = inject(Router);
  protected readonly kickService = inject(KickService);
  private readonly dialogService = inject(DialogService);
  private readonly archiveService = inject(ArchiveService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly pinnedMessagesService = inject(PinnedMessageService);

  pinnedMessages$ = this.pinnedMessagesService.pins$;

  get roomname(): string | undefined {
    const currRoom = this.roomListAccessService.getCurrentModelRoom();
    return this.roomHelperService.getRoomname(currRoom?.roomId, currRoom?.name);
  }
  getRoomCreator(): string | null {
    const currRoom = this.roomListAccessService.getCurrentModelRoom();
    if (!currRoom) {
      return null;
    }

    const creatorId = currRoom.creator;
    if (!creatorId) return null;

    const member = this.roomListAccessService.getModelRoomMember(currRoom.roomId, creatorId);

    return member?.name ?? null;
  }

  toggleSidebar(): void {
    this.sidebarOpenedChange.emit(!this.opened());
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer!.dropEffect = 'none';
  }

  onKeyDown(event: KeyboardEvent, messageId: string): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.scrollToElement(messageId);
    }
  }

  /**
   * Entfernt einen User aus dem Chat
   * @param member
   */
  public async kick(member: RoomMember): Promise<void> {
    const room = this.roomListAccessService.getCurrentModelRoom();
    if (!room) return;
    await this.kickService.openKickDialog(room.roomId, member.mxid);
  }

  openDialog(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.isRoomMember()) {
      this.router.navigate(['rooms', this.chatStoreService.getStateSync().currentRoomId, 'settings']);
    }
  }

  scrollToElement(id: string): void {
    const element = document.getElementById(id);
    if (!element) {
      this.loadAllMessages(this.chatStoreService.getStateSync().currentRoomId).subscribe({
        next: () => {
          const element = document.getElementById(id);
          if (element) {
            // Zum Element scrollen
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      });
    } else {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  handleUserClick(mxid: string): void {
    this.selectedMemberId = this.selectedMemberId === mxid ? null : mxid;
  }

  showHoverProfile(mxid: string): void {
    if (!this.selectedMemberId) {
      this.hoverTimeout = setTimeout(() => {
        this.hoveredMemberId = mxid;
      }, 300);
    }
  }

  hideHoverProfile(): void {
    clearTimeout(this.hoverTimeout);
    this.hoveredMemberId = null;
  }

  onProfileClosed(): void {
    this.selectedMemberId = null;
    this.hoveredMemberId = null;
  }

  /****************************************************************************
   * leaveAndArchiveRoom
   ****************************************************************************/

  leaveRoom(room: ModelRoom): void {
    const userId = this.clientService.getUserId();
    const hasOtherModerator = room.members.filter(m => m.mxid !== userId).some(member => member.powerLevel >= 50);
    this.leaveAndArchiveRoom(room, userId, hasOtherModerator).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  /****************************************************************************
   * forgetRoom
   ****************************************************************************/

  forgetRoom(room: ModelRoom): void {
    if (!room) {
      throw new Error('Raum kann nicht verlassen werden, da der Raum nicht gesetzt ist');
    }

    blockRoom(room);
    this.handleForgetDialog().subscribe(async forget => {
      if (forget) {
        try {
          await this.roomService.forgetRoom(room.roomId);
          await this.router.navigate(['rooms']);
        } catch (e) {
          console.warn('forgetRoom fehlgeschlagen', e);
        }
      }
      unblockRoom(room);
    });
  }

  /**
   * Stößt die Archivierung an. Falls dabei ein Pdf zurückkommt, wird diese heruntergeladen.
   */
  archive(room: ModelRoom): void {
    room.tags['isBlocked'] = { inProcess: true };

    this.archiveService.archiving(room.roomId).subscribe({
      next: response => {
        this.archiveService.handleArchivingResponse(room, response);
      },
      error: () => {
        this.snackbarService.error('Archivierung abgebrochen');
        room.tags['isBlocked'] = { inProcess: false };
      },
      complete: () => {
        this.snackbarService.success('Archivierung erfolgreich durchgeführt');
        room.tags['isBlocked'] = { inProcess: false };
      }
    });
  }

  /****************************************************************************
   * Helper
   ****************************************************************************/

  isRoomMember(): boolean {
    const roomId = this.chatStoreService.getStateSync().currentRoomId;

    if (!roomId) return false;

    return this.roomHelperService.getRoomMembership(roomId) === KnownMembership.Join;
  }

  historyVisibilityToText(roomHV: RoomHistoryVisibility | undefined): string {
    if (!roomHV) return '';
    switch (roomHV) {
      case RoomHistoryVisibility.Invited:
        return 'Nachrichtenverlauf: Ab Einladung';
      case RoomHistoryVisibility.Joined:
        return 'Nachrichtenverlauf: Ab Beitritt';
      case RoomHistoryVisibility.Shared:
        return 'Nachrichtenverlauf: Vollständig';
      case RoomHistoryVisibility.WorldReadable:
        return 'Nachrichtenverlauf: Jeder';
      default:
        return '';
    }
  }

  historyVisibilityTooltip(roomHV: RoomHistoryVisibility | undefined): string {
    if (!roomHV) return '';
    switch (roomHV) {
      case RoomHistoryVisibility.Invited:
        return 'Eingeladene Mitglieder sehen den historischen Nachrichtenverlauf ab dem Zeitpunkt der Einladung';
      case RoomHistoryVisibility.Joined:
        return 'Neue Mitglieder sehen den historischen Nachrichtenverlauf ab dem Zeitpunkt des Beitritt';
      case RoomHistoryVisibility.Shared:
        return 'Neue Mitglieder sehen den historischen Nachrichtenverlauf';
      case RoomHistoryVisibility.WorldReadable:
        return 'Jeder kann ggf. den historischen Nachrichtenverlauf einsehen (auch ohne Beitritt)';
      default:
        return '';
    }
  }

  /**
   * lädt alle Events zu einem Raum.
   * @param roomId
   * @private
   */
  /** @deprecated */
  private loadAllMessages(roomId: string | undefined): Observable<MatrixEvent[]> {
    if (!roomId) {
      return throwError(() => new Error('RoomId was undefined when calling loadAllMessages'));
    }
    const room = this.clientService.client.getRoom(roomId);
    if (!room) {
      return throwError(() => new Error('Raum nicht gefunden'));
    }

    const timelineSet = room.getTimelineSets()[0];
    const timelineWindow = new TimelineWindow(this.clientService.client, timelineSet);
    return from(timelineWindow.load()).pipe(
      expand(() => {
        return timelineWindow.canPaginate(EventTimeline.BACKWARDS)
          ? from(timelineWindow.paginate(EventTimeline.BACKWARDS, 50)) // Lade 50 Nachrichten pro Anfrage
          : EMPTY;
      }),
      map(() => timelineWindow.getEvents()) // Gibt alle geladenen Nachrichten zurück
    );
  }

  /**
   * Verlässt den Raum. Bei Bestätigung des Verlassens wird eine Archivierung ausgeführt.
   * Ist diese erfolgreich, wird der Raum verlassen.
   * A_26463 - Sperrung der weiterführenden Kommunikation nach Verlassen des Raumes
   */
  private leaveAndArchiveRoom(
    room: ModelRoom | null | undefined,
    userId: string,
    hasOtherModerator: boolean
  ): Observable<void | Blob | null> {
    if (!room) {
      throw new Error('Raum kann nicht verlassen werden, da der Raum nicht gesetzt ist');
    }
    blockRoom(room);
    return this.handleLeaveDialog(hasOtherModerator, room.members).pipe(
      switchMap(resp => this.handleLeavingResponse(room, resp, userId, hasOtherModerator))
    );
  }

  // A_26463 - Sperrung der weiterführenden Kommunikation nach Verlassen des Raumes
  private handleLeaveDialog(hasOtherModerator: boolean, roomMembers: RoomMember[]): Observable<boolean> {
    let leavingDialogData: DialogData;
    const joinedOrInvitedMembers = roomMembers.filter(
      member =>
        member.membershipState === RoomMembershipState.Join || member.membershipState === RoomMembershipState.Invite
    );
    leavingDialogData = {
      title: 'Chat verlassen',
      message: 'Möchten sie den Chat endgültig verlassen?',
      subMessage: `Nach dem Verlassen können Sie keine Nachrichten mehr senden oder empfangen. Damit Ihr Gesprächsverlauf
       nicht verloren geht, wird Ihnen beim Verlassen automatisch eine PDF-Datei zum Download bereitgestellt.`,
      confirmText: 'Chat verlassen',
      cancelText: 'Abbrechen',
      svgIcon: 'circle-error',
      cancelDataTestId: RoomSidebarTestIds.buttonCancelLeaveroom,
      confirmDataTestId: RoomSidebarTestIds.buttonConfirmLeaveroom,
      dialogContentTestId: RoomSidebarTestIds.dialogLeavingContent,
      dialogActionTestId: RoomSidebarTestIds.dialogLeavingActions
    };
    if (joinedOrInvitedMembers.length > 1 && !hasOtherModerator) {
      leavingDialogData = {
        title: 'Chat verlassen',
        message: 'Möchten sie den Chat endgültig verlassen?',
        subMessage:
          'Da Sie den aktuellen Chat moderieren, wird der Chat mit ' +
          'Ihrem Verlassen für alle Teilnehmende geschlossen. Eine weitere Kommunikation in diesem Chat ist danach nicht mehr möglich.',
        confirmText: 'Ja, Chat verlassen und schließen',
        cancelText: 'Abbrechen',
        svgIcon: 'circle-error',
        cancelDataTestId: RoomSidebarTestIds.buttonCancelLeaveroom,
        confirmDataTestId: RoomSidebarTestIds.buttonConfirmLeaveroom,
        dialogContentTestId: RoomSidebarTestIds.dialogLeavingContent,
        dialogActionTestId: RoomSidebarTestIds.dialogLeavingActions
      };
      return this.dialogService.openErrorDialog(leavingDialogData, '28.6rem');
    }
    return this.dialogService.openErrorDialog(leavingDialogData, '28.6rem');
  }

  private handleLeavingResponse(
    room: ModelRoom,
    leave: boolean,
    userId: string,
    hasOtherModerator: boolean
  ): Observable<void | Blob | null> {
    if (!leave) {
      unblockRoom(room);
      return of(null); // oder EMPTY, wenn du nichts zurückgeben willst
    }
    blockRoom(room);

    return this.archiveService.archiving(room.roomId).pipe(
      tap({
        next: res => this.archiveService.handleArchivingResponse(room, res),
        error: err => console.error('Fehler im tap handleLeaving', err)
      }),
      finalize(async () => {
        const kickResult = await this.kickOtherMembersIfRequired(room, userId, hasOtherModerator);
        unblockRoom(room);
        if (kickResult.cancelled) {
          return;
        }
        if (kickResult.kickFailed) {
          this.snackbarService.error(
            'Verlassen wurde abgebrochen, da mindestens ein User nicht entfernt werden konnte.'
          );
        } else {
          await this.roomService.leaveRoom(room.roomId);
        }
      }),
      catchError(err => {
        unblockRoom(room);
        return throwError(() => err);
      })
    );
  }

  /****************************************************************************
   * archive
   ****************************************************************************/

  private async kickOtherMembersIfRequired(
    room: ModelRoom,
    userId: string,
    hasOtherModerator: boolean
  ): Promise<{ kickFailed: boolean; cancelled: boolean }> {
    if (hasOtherModerator) {
      return { kickFailed: false, cancelled: false };
    }

    const memberMxIdsToKick = room.members
      .filter(
        member =>
          userId !== member.mxid &&
          (member.membershipState === KnownMembership.Join || member.membershipState === KnownMembership.Invite)
      )
      .map(member => member.mxid);

    if (memberMxIdsToKick.length === 0) {
      return { kickFailed: false, cancelled: false };
    }

    try {
      await this.kickService.openKickRoomMembersDialog(room.roomId, memberMxIdsToKick);
      return { kickFailed: false, cancelled: false };
    } catch {
      return { kickFailed: true, cancelled: false };
    }
  }

  private handleForgetDialog(): Observable<boolean> {
    const forgetDialogData: DialogData = {
      title: 'Chat wirklich löschen?',
      message:
        'Wollen Sie diesen Chat endgültig löschen? Der Chat erscheint im Anschluss nicht mehr in der Chatübersicht. ' +
        'Bitte stellen Sie sicher, dass Sie den Chatverlauf bei Bedarf vorab exportiert und in Ihr Archivsystem (DMS) ' +
        'übertragen haben, da die Inhalte danach nicht mehr im TI-M Pro Client abrufbar sind.',
      confirmText: 'Chat löschen',
      cancelText: 'Abbrechen',
      svgIcon: 'circle-error',
      cancelDataTestId: 'button_cancel_forgetroom',
      confirmDataTestId: 'button_confirm_forgetroom',
      dialogActionTestId: 'action_forgetroom',
      dialogContentTestId: 'content_forgetroom'
    };
    return this.dialogService.openErrorDialog(forgetDialogData, '28.6rem', '21.8rem');
  }
}
